import { EventEmitter } from "events";
import bindAllOfThis from "src/utils/BindThisHelper";

class GenericKey extends EventEmitter {
    protected isPressed: boolean = false;
    /** No longer used */
    public key: string;

    public isDown(): boolean {
        return this.isPressed;
    }
    public isUp(): boolean {
        return !this.isDown;
    }

    constructor(key: string) {
        super();
        bindAllOfThis(this, GenericKey.prototype);
        this.key = key.toUpperCase();
    }
}

class ImageKey extends GenericKey {
    protected pointerId: number = 99;

    constructor(inputPlugin: Phaser.Input.InputPlugin, keyString: string, image: Phaser.GameObjects.Image) {
        super(keyString);
        bindAllOfThis(this, ImageKey.prototype);

        image.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
            this.pointerId = pointer.id;
            this.isPressed = true;
            this.emit("fancy_keydown", this.key);
        });
        inputPlugin.on("pointerup", (pointer: Phaser.Input.Pointer) => {
            if (pointer.id === this.pointerId) {
                this.isPressed = false;
                this.pointerId = 99;
                this.emit("fancy_keyup", this.key);
            }
        });
    }
}

class KeyboardKey extends GenericKey {
    // constructor(inputPlugin: Phaser.Input.InputPlugin, key: Phaser.Input.Keyboard.Key, keyString: string) {
    constructor(inputPlugin: Phaser.Input.InputPlugin, keyString: string) {
        super(keyString);
        bindAllOfThis(this, KeyboardKey.prototype);

        // This has to be done or the event will not fire... maybe
        // inputPlugin.keyboard.addKey(key);
        inputPlugin.keyboard.on(`keydown_${keyString.toUpperCase()}`, () => {
            this.isPressed = true;
            this.emit("fancy_keydown", this.key);
        });
        inputPlugin.keyboard.on(`keyup_${keyString.toUpperCase()}`, () => {
            this.isPressed = false;
            this.emit("fancy_keyup", this.key);
        });
    }
}

class KeyCollection extends EventEmitter {
    protected collection: GenericKey[] = [];
    protected alreadyTriggered: boolean;
    protected isPressed: boolean = false;

    public get isDown(): boolean {
        return this.isPressed;
    }
    public get isUp(): boolean {
        return !this.isPressed;
    }

    constructor() {
        super();
        bindAllOfThis(this, KeyCollection.prototype);
    }

    private updatePressedState() {
        // This is purely an optimization function
        // Putting this into 'isDown()' would cause it to run atleast once every frame
        this.isPressed = this.collection.reduce((first: boolean, second: GenericKey) => {
            return first || second.isDown();
        }, false);
    }

    addKey(newKey: ImageKey | KeyboardKey) {
        newKey.on("fancy_keydown", this.handleKeyDown);
        newKey.on("fancy_keyup", this.handleKeyUp);
        this.collection.push(newKey);
    }

    protected handleKeyDown() {
        if (!this.alreadyTriggered) {
            this.alreadyTriggered = true;
            this.updatePressedState();
            this.emit(`keydown`);
        }
    }

    protected handleKeyUp() {
        // If we are still in the 'down' state, we do not want to trigger a 'keyup' event
        // The event *should* occur after the properties are set. Crossing my fingers on this one
        this.updatePressedState();
        if (this.isUp) {
            this.alreadyTriggered = false;
            this.emit(`keyup`);
        }
    }
}

class GamePadKeys {
    up: KeyCollection;
    down: KeyCollection;
    left: KeyCollection;
    right: KeyCollection;
    a: KeyCollection;
    b: KeyCollection;

    constructor() {
        this.up = new KeyCollection();
        this.down = new KeyCollection();
        this.left = new KeyCollection();
        this.right = new KeyCollection();
        this.a = new KeyCollection();
        this.b = new KeyCollection();
    }
}

export class PhaserGamePadConfig {
    scale: number = 1.25;
    texture: "normal" | "inverted" = "normal";
    enableArrowKeys: boolean = true;
    enableButtons: boolean = true;
    showOnScreenDisplayOnDesktop: boolean = false;
}

class GamePadTheme {
    up: string;
    down: string;
    left: string;
    right: string;
    a: string;
    b: string;
}

export class PhaserGamePad extends Phaser.GameObjects.Container {
    private themes = {
        normal: { up: "up", down: "down", left: "left", right: "right", a: "a", b: "b", } as GamePadTheme,
        inverted: { up: "up_inverted", down: "down_inverted", left: "left_inverted", right: "right_inverted", a: "a_inverted", b: "b_inverted", } as GamePadTheme
    };
    private theme: GamePadTheme;
    private config: PhaserGamePadConfig;
    private arrowSize: number;
    private isTouchAvailable: boolean;

    /**
     * A generated 'keys' object that contains values indicating if the key is pressed or not. This is for use in the update loop
     *
     * See Phaser.Input.Keyboard.Key or CursorKeys
     */
    public keys: GamePadKeys;

    private ARROW_CONST: number = 64;
    private GAMEPAD_ALPHA = .66;

    constructor(scene: Phaser.Scene, config: PhaserGamePadConfig = new PhaserGamePadConfig()) {
        super(scene, 0, 0);
        bindAllOfThis(this, PhaserGamePad.prototype);

        this.config = config;
        this.arrowSize = this.ARROW_CONST * config.scale;
        this.isTouchAvailable = this.scene.sys.game.device.input.touch;

        // Multitouch support!
        if(this.isTouchAvailable) {
            this.scene.input.addPointer(6);
        }
        this.keys = new GamePadKeys();

        this.initialize();
    }

    public static preload(scene: Phaser.Scene): void {
        scene.load.atlas("gamePad_assets", require("src/assets/qci/atlas/gamepad.png"), require("src/assets/qci/atlas/gamepad.json"));
    }

    initialize(): void {
        // Setup the container
        this.setSize(this.scene.sys.canvas.width, this.arrowSize * 3.25);
        this.setPosition(0, this.scene.sys.canvas.height - this.height);
        this.setAlpha(this.GAMEPAD_ALPHA);
        this.setDepth(1000); // This solves overlay issues

        // Setup theme
        this.configureTheme();

        // Construct Objects
        this.createOnscreenKeyboard();
        this.bindPhysicalKeyboard();

        // Bind events
        this.bindEvents();

        // Add items to self and self to scene
        this.scene.add.existing(this);
    }

    configureTheme() {
        switch (this.config.texture) {
            case "normal":
                this.theme = this.themes.normal;
                break;
            case "inverted":
                this.theme = this.themes.inverted;
                break;
            default:
                // The following line guarantees that every action is accounted for #magic
                const exhaustiveCheck: never = this.config.texture;
        }
    }

    bindPhysicalKeyboard() {
        // Run through the set of 'keyboards' we want
        this.keys.up.addKey(new KeyboardKey(this.scene.input, "up"));
        this.keys.down.addKey(new KeyboardKey(this.scene.input, "down"));
        this.keys.left.addKey(new KeyboardKey(this.scene.input, "left"));
        this.keys.right.addKey(new KeyboardKey(this.scene.input, "right"));

        this.keys.a.addKey(new KeyboardKey(this.scene.input, "space"));

        this.keys.b.addKey(new KeyboardKey(this.scene.input, "enter"));
        this.keys.b.addKey(new KeyboardKey(this.scene.input, "e"));


        this.keys.up.addKey(new KeyboardKey(this.scene.input, "w"));
        this.keys.down.addKey(new KeyboardKey(this.scene.input, "s"));
        this.keys.left.addKey(new KeyboardKey(this.scene.input, "a"));
        this.keys.right.addKey(new KeyboardKey(this.scene.input, "d"));
    }

    createOnscreenKeyboard() {
        // Dont bind onscreen keys if we are not showing them
        if (!this.isTouchAvailable && !this.config.showOnScreenDisplayOnDesktop) {
            return;
        }

        if (this.config.enableArrowKeys) {
            this.keys.up.addKey(new ImageKey(this.scene.input, "up", this.configureImage(this.theme.up, this.arrowSize * 1.5, 0)));
            this.keys.down.addKey(new ImageKey(this.scene.input, "down", this.configureImage(this.theme.down, this.arrowSize * 1.5, this.arrowSize * 2)));
            this.keys.left.addKey(new ImageKey(this.scene.input, "left", this.configureImage(this.theme.left, this.arrowSize * 0.5, this.arrowSize * 1)));
            this.keys.right.addKey(new ImageKey(this.scene.input, "right", this.configureImage(this.theme.right, this.arrowSize * 2.5, this.arrowSize * 1)));
        }

        if (this.config.enableButtons) {
            this.keys.a.addKey(new ImageKey(this.scene.input, "a", this.configureImage(this.theme.a, this.width - this.arrowSize * 1.5, this.arrowSize * 0.5)));
            this.keys.b.addKey(new ImageKey(this.scene.input, "b", this.configureImage(this.theme.b, this.width - this.arrowSize * 3, this.arrowSize * 1.75)));
        }
    }

    configureImage(texture: string, x: number, y: number) {
        let gameObject = this.scene.add.image(x, y, "gamePad_assets", texture);
        gameObject.setOrigin(0);
        gameObject.setInteractive();
        gameObject.setScrollFactor(0);
        gameObject.setDisplaySize(this.arrowSize, this.arrowSize);
        gameObject.setDepth(2);

        this.add(gameObject);
        return gameObject;
    }

    bindEvents(): void {
        this.keys.up.on("keydown", () => {
            this.emit("keydown_up");
        });
        this.keys.up.on("keyup", () => {
            this.emit("keyup_up");
        });

        this.keys.down.on("keydown", () => {
            this.emit("keydown_down");
        });
        this.keys.down.on("keyup", () => {
            this.emit("keyup_down");
        });

        this.keys.left.on("keydown", () => {
            this.emit("keydown_left");
        });
        this.keys.left.on("keyup", () => {
            this.emit("keyup_left");
        });

        this.keys.right.on("keydown", () => {
            this.emit("keydown_right");
        });
        this.keys.right.on("keyup", () => {
            this.emit("keyup_right");
        });

        this.keys.a.on("keydown", () => {
            this.emit("keydown_a");
        });
        this.keys.a.on("keyup", () => {
            this.emit("keyup_a");
        });

        this.keys.b.on("keydown", () => {
            this.emit("keydown_b");
        });
        this.keys.b.on("keyup", () => {
            this.emit("keyup_b");
        });


    }
}



// WEBPACK FOOTER //
// ./src/games/shared/Gamepad.ts